/*
    slick-a11y.js
    ------------------------------------------------------------------
    Replaces Slick 1.8's initADA() and updateDots() with versions that
    emit valid ARIA.

    Almost every ARIA finding Lighthouse reports on this store's carousels
    comes out of initADA() (updateDots() is the exception -- see its own
    note further down). Slick writes:

        <div class="slick-track" role="listbox">          <- audit 3
          <div class="slick-slide" role="option" ...>
        <ul class="slick-dots" role="tablist">            <- audit 2
          <li role="presentation" aria-selected="false"   <- audits 1 + 4
              aria-controls="navigation01" id="slick-slide01">

    and it re-writes all of it after every slide change, so patching the
    markup from outside gets undone on the next swipe. Patching the method
    fixes it once, for every slider on the page, permanently.

    What is wrong with it:

      * role="listbox" / role="option" -- listbox is a form widget. It
        requires an accessible name (which the track has none of) and real
        option semantics. A carousel is not a listbox.
      * role="tablist" on the dots whose children are role="presentation"
        -- a tablist must own elements with role="tab". There are none.
      * role="presentation" on an <li> that also carries aria-selected,
        aria-controls and aria-hidden -- global ARIA attributes keep the
        element in the accessibility tree, so the presentation role never
        resolves. That is the "presentation role conflict" audit.
      * aria-controls="navigation<uid><i>" points at an id that Slick never
        renders on anything.

    What this emits instead: no invented widget roles, and aria-current on
    the dot for the slide currently on screen -- which is the one piece of
    state a pager actually needs to expose. Slides keep aria-hidden and
    tabindex="-1" while off-screen; Slick already got that part right.

    Loaded straight after vendor.min.js (both defer, so order is
    guaranteed), which puts it in place before any slider initialises.
*/
(function () {
    'use strict';

    function install($) {
        if (!$ || !$.fn || !$.fn.slick || $.fn.slick.__a11yWrapped) { return; }

        /* --------------------------------------------------------------
           The replacement. Runs with `this` bound to a Slick instance, on
           init and again after every slide change.
        -------------------------------------------------------------- */
        function initADA() {
            var slick = this;
            var $cloned = slick.$slideTrack.find('.slick-cloned');
            var $allSlides = slick.$slides.add($cloned);

            /* Off-screen slides stay hidden and unreachable by tab. */
            $allSlides
                .attr({ 'aria-hidden': 'true', tabindex: '-1' })
                .find('a, input, button, select').attr({ tabindex: '-1' });

            /* Drop the listbox/option pair outright. */
            slick.$slideTrack.removeAttr('role');
            $allSlides.removeAttr('role aria-describedby');

            if (slick.$dots !== null && slick.$dots.length) {
                /* A pager is a list of controls, not a tablist. */
                slick.$dots.removeAttr('role');

                slick.$dots.find('li').each(function () {
                    var $dot = $(this);

                    $dot.removeAttr('role aria-selected aria-controls id aria-hidden');

                    /* Default paging renders <button>; halo.slide-show.js
                       renders <a class="dot">. Label whichever is there. */
                    var $control = $dot.children('button, a').first();
                    var $target = $control.length ? $control : $dot;

                    if ($dot.hasClass('slick-active')) {
                        $target.attr('aria-current', 'true');
                    } else {
                        $target.removeAttr('aria-current');
                    }
                });

                /* Slick stamps role="toolbar" on whatever <div> wraps the
                   dots -- normally a plain theme layout div. */
                var $wrap = slick.$dots.closest('div');
                if ($wrap.attr('role') === 'toolbar') { $wrap.removeAttr('role'); }
            }

            slick.activateADA();
        }

        /* --------------------------------------------------------------
           Slick's own updateDots() runs on init and after every slide
           change -- independently of initADA -- and does this:

               $dots.find('li').removeClass('slick-active')
                               .attr('aria-hidden', 'true');
               $dots.find('li').eq(active).addClass('slick-active')
                               .attr('aria-hidden', 'false');

           So every pager <li> except the current one is marked
           aria-hidden while the <button> inside it stays in the tab
           order. That is the "ARIA hidden element must not be focusable
           or contain focusable elements" audit, and it reports the bare
           <li class="" aria-hidden="true"> that removeClass leaves
           behind. Cleaning it up from initADA is not enough -- the next
           swipe writes it straight back, so the method itself has to go.

           A pager dot is a visible control. It should never be hidden
           from assistive tech; which one is current is carried by
           aria-current, same as initADA sets.
        -------------------------------------------------------------- */
        function updateDots() {
            var slick = this;

            if (slick.$dots === null || !slick.$dots.length) { return; }

            var active = Math.floor(slick.currentSlide / slick.options.slidesToScroll);

            slick.$dots.find('li').each(function (index) {
                var $dot = $(this);
                var $control = $dot.children('button, a').first();
                var $target = $control.length ? $control : $dot;

                $dot.removeAttr('aria-hidden');

                if (index === active) {
                    $dot.addClass('slick-active');
                    $target.attr('aria-current', 'true');
                } else {
                    $dot.removeClass('slick-active');
                    $target.removeAttr('aria-current');
                }
            });
        }

        /* --------------------------------------------------------------
           Slick keeps its constructor inside a closure, so the only route
           to the prototype is an instance -- and $.fn.slick parks one on
           each element it initialises (element.slick). Wrap the plugin,
           take the prototype from the first instance it produces, then get
           out of the way.
        -------------------------------------------------------------- */
        function resweep() {
            $('.slick-initialized').each(function () {
                var instance = this.slick;
                if (instance && instance.options && instance.options.accessibility) {
                    try { instance.initADA(); } catch (err) {}
                }
            });
        }

        function patchFrom(instance) {
            var proto = instance && Object.getPrototypeOf(instance);
            if (!proto || proto.__a11yPatched) { return false; }

            proto.__a11yPatched = true;
            proto.initADA = initADA;
            proto.updateDots = updateDots;

            /* Anything already on the page ran the old version. */
            resweep();
            return true;
        }

        var nativePlugin = $.fn.slick;

        $.fn.slick = function () {
            var result = nativePlugin.apply(this, arguments);

            for (var i = 0; i < this.length; i++) {
                if (this[i] && this[i].slick && patchFrom(this[i].slick)) {
                    /* Done -- the prototype is patched for every slider
                       from here on, so stop paying for the wrapper. */
                    $.fn.slick = nativePlugin;
                    break;
                }
            }

            return result;
        };

        $.fn.slick.__a11yWrapped = true;

        /* Fallback: a slider initialised by an app before this ran. */
        $(function () {
            var first = document.querySelector('.slick-initialized');
            if (first && first.slick) {
                if (patchFrom(first.slick)) { $.fn.slick = nativePlugin; }
            }
        });
    }

    if (window.jQuery) {
        install(window.jQuery);
    } else {
        /* vendor.min.js should already be in, but never assume. */
        document.addEventListener('DOMContentLoaded', function () {
            install(window.jQuery);
        }, { once: true });
    }
}());
